import PageHeading from '../../components/PageHeading'
import CountCard from '../../components/dashboard/CountCard';
import topM from "../../assets/images/top.jpg";

import { Row, Col, message, Typography, Table } from 'antd';
import { 
  DashboardFilled,  
  StarFilled,
  PieChartFilled,
  HeartFilled,
  SmileFilled
} from '@ant-design/icons';
import Spacer from '../../components/miscellaneous/Spacer';
import { useEffect, useState } from 'react';
import axiosInstance from '../../utils/axios';
import TopMovie from '../../components/dashboard/TopMovie';

const { Title } = Typography;

const Dashboard = () => {

  const [loading, setLoading] = useState(false);

  const [counts, setCounts] = useState({
    section_sales: {},
    top_selling_event: {},
    top_selling_section: {}
  });

  const [topMovie, setTopMovie] = useState({});
  const [theatreReport, setTheatreReport] = useState([]);
  const [movieReport, setMovieReport] = useState([]);
  const [eventRevenueReport, setEventRevenueReport] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {

      /* ================= DASHBOARD COUNTS ================= */

      const cRes = await axiosInstance.get("dashboard/count/");
      setCounts(cRes.data);

      const topRes = await axiosInstance.get("dashboard/top-movie/");
      setTopMovie(topRes.data);

      const tRes = await axiosInstance.get("dashboard/theatre-report/");
      setTheatreReport(tRes.data);

      const mRes = await axiosInstance.get("dashboard/movie-report/");
      setMovieReport(mRes.data);

      /* ================= FETCH ALL EVENT SNAPSHOTS ================= */

      let allSnapshots = [];
      let page = 1;
      let hasNext = true;

      while (hasNext) {
        const snapRes = await axiosInstance.get(
          `news_and_events/event-ticket-snapshots/?page=${page}`
        );

        allSnapshots = [...allSnapshots, ...snapRes.data.results];

        if (snapRes.data.next) {
          page++;
        } else {
          hasNext = false;
        }
      }

      /* ================= GROUP BY EVENT ================= */

      const grouped = {};

      allSnapshots.forEach(item => {

        // ignore unpaid bookings
        if (!item.paid) return;

        const eventKey = item.event_id;
        const eventName = item.event_title || "Unknown Event";
        const amount = Number(item.amount) || 0;

        if (!grouped[eventKey]) {
          grouped[eventKey] = {
            event_name: eventName,
            total_revenue: 0,
            total_bookings: 0,
            tickets_sold: 0,
            sections: new Set()
          };
        }

        // booking count
        grouped[eventKey].total_bookings += 1;

        // revenue
        grouped[eventKey].total_revenue += amount;

        // tickets sold
        grouped[eventKey].tickets_sold += Number(item.total_tickets) || 0;

        // sections
        if (Array.isArray(item.sections)) {
          item.sections.forEach(section => {
            if (section?.title) {
              grouped[eventKey].sections.add(section.title);
            }
          });
        }

      });

      const formattedRevenue = Object.values(grouped).map((item, index) => ({
        id: index,
        event_name: item.event_name,
        total_bookings: item.total_bookings,
        tickets_sold: item.tickets_sold,
        sections: Array.from(item.sections).join(", "),
        total_revenue: Number(item.total_revenue.toFixed(2))
      }));

      setEventRevenueReport(formattedRevenue);

    } catch (err) {
      message.error("Cannot fetch some data");
    } finally {
      setLoading(false);
    }
  }

  /* ================= THEATRE REPORT ================= */

  const tColumns = [
    { title: 'Name', dataIndex: 'theatre_name', key: 'theatre_name' },
    { title: 'Screen Count', dataIndex: 'screen_count', key: 'screen_count' },
    { title: 'Weekly', dataIndex: 'weekly_profit', key: 'weekly_profit' },
    { title: 'Monthly', dataIndex: 'monthly_profit', key: 'monthly_profit' },
    { title: 'Yearly', dataIndex: 'yearly_profit', key: 'yearly_profit' },
  ];

  /* ================= MOVIE REPORT ================= */

  const mColumns = [
    { title: 'Name', dataIndex: 'movie_name', key: 'movie_name' },
    { title: 'Language', dataIndex: 'language', key: 'language' },
    { title: 'Weekly', dataIndex: 'weekly_profit', key: 'weekly_profit' },
    { title: 'Monthly', dataIndex: 'monthly_profit', key: 'monthly_profit' },
  ];

  /* ================= SECTION SALES ================= */

  const sectionSalesData = counts?.section_sales
    ? Object.entries(counts.section_sales).map(([key, value], index) => ({
        id: index,
        section: key,
        tickets_sold: value
      }))
    : [];

  const sectionColumns = [
    { title: 'Section', dataIndex: 'section', key: 'section' },
    { title: 'Tickets Sold', dataIndex: 'tickets_sold', key: 'tickets_sold' },
  ];

  /* ================= EVENT REVENUE REPORT ================= */

  const eventRevenueColumns = [
    { title: 'Event Name', dataIndex: 'event_name', key: 'event_name' },

    { title: 'Sections', dataIndex: 'sections', key: 'sections' },

    { title: 'Total Bookings', dataIndex: 'total_bookings', key: 'total_bookings' },

    { title: 'Tickets Sold', dataIndex: 'tickets_sold', key: 'tickets_sold' },

    {
      title: 'Total Revenue',
      dataIndex: 'total_revenue',
      key: 'total_revenue',
      render: (value) => `${value.toFixed(2)}`
    }
  ];

  return (
    <div className='dashboard'>

      <PageHeading name="Dashboard" icon={<DashboardFilled/>}/>

      {/* ================= COUNT CARDS + TOP MOVIE ================= */}

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={24} md={12} lg={12}>
          <Row gutter={[16, 16]}>

            <Col xs={24} lg={12}>
              <CountCard 
                title="Total Theatres" 
                count={counts?.theaters || 0} 
                icon={<PieChartFilled />}
              />
            </Col>

            <Col xs={24} lg={12}>
              <CountCard 
                title="Now Showing" 
                count={counts?.now_showing || 0} 
                icon={<StarFilled />}
              />
            </Col>

            <Col xs={24} lg={12}>
              <CountCard 
                title="Upcoming Movies" 
                count={counts?.upcoming_movies || 0} 
                icon={<HeartFilled />} 
              />
            </Col>

            <Col xs={24} lg={12}>
              <CountCard 
                title="Customers" 
                count={counts?.customers || 0} 
                icon={<SmileFilled />}
              />
            </Col>

            <Col xs={24} lg={12}>
              <CountCard 
                title="Event Bookings" 
                count={counts?.event_bookings || 0} 
                icon={<SmileFilled />}
              />
            </Col>

            <Col xs={24} lg={12}>
              <CountCard 
                title="Top Selling Event" 
                count={
                  counts?.top_selling_event?.event_title
                    ? `${counts.top_selling_event.event_title} (${counts.top_selling_event.total_tickets_sold})`
                    : "N/A"
                }
                icon={<SmileFilled />}
              />
            </Col>

            <Col xs={24} lg={12}>
              <CountCard 
                title="Top Selling Section" 
                count={
                  counts?.top_selling_section?.title
                    ? `${counts.top_selling_section.title} (${counts.top_selling_section.total_sold})`
                    : "N/A"
                }
                icon={<StarFilled />}
              />
            </Col>

          </Row>
        </Col>

        <Col xs={24} lg={12}>
          <TopMovie topM={topM} topMovie={topMovie}/>
        </Col>
      </Row>

      <Spacer height="30px"/>

      {/* ================= REPORT TABLES ================= */}

      <Row gutter={[16, 16]}>

        <Col sm={24} lg={12}>
          <Title level={3}>Theatre Profit Report</Title>
          <Table
            dataSource={theatreReport}
            loading={loading}
            columns={tColumns}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            pagination
          />
        </Col>

        <Col sm={24} lg={12}>
          <Title level={3}>Movie Profit Report</Title>
          <Table
            dataSource={movieReport}
            loading={loading}
            columns={mColumns}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            pagination
          />
        </Col>

      </Row>

      <Spacer height="30px"/>

      {/* ================= SECTION SALES + EVENT REVENUE ================= */}

      <Row gutter={[16, 16]}>

        <Col span={24}>
          <Title level={3}>Event Section Sales</Title>
          <Table
            dataSource={sectionSalesData}
            loading={loading}
            columns={sectionColumns}
            rowKey="id"
            pagination={false}
          />
        </Col>
      </Row>

      <Row gutter={[16,16]}>
        <Col span={24}>
          <Title level={3}>Event Revenue Report</Title>
          <Table
            dataSource={eventRevenueReport}
            loading={loading}
            columns={eventRevenueColumns}
            rowKey="id"
            scroll={{ x: 'max-content' }}
            pagination={false}
          />
        </Col>
      </Row>

    </div>
  )
}

export default Dashboard;